import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { useTable } from "@refinedev/react-table";
import {
  useGo,
  useNavigation,
  type HttpError,
} from "@refinedev/core";
import { Shield } from "@/components/ui/icon";
import type { Role } from "@aos/shared";

import {
  DataTable,
  PageHeader,
  type RefineTableCore,
} from "../projects/_shared";
import { ProposedListPage } from "../_shared/ProposedModule";
import { RowActions } from "../_shared/ListScaffold";
import { humanize } from "../projects/_form";

export function RoleList() {
  const go = useGo();
  const { showUrl, editUrl } = useNavigation();

  const columns = useMemo<ColumnDef<Role, unknown>[]>(
    () => [
      {
        id: "name",
        accessorKey: "name",
        header: "Role",
        cell: ({ row }) => (
          <span className="font-medium">{humanize(row.original.name)}</span>
        ),
      },
      {
        id: "description",
        accessorKey: "description",
        header: "Description",
        enableSorting: false,
        cell: ({ row }) => (
          <span className="line-clamp-1 max-w-md text-muted-foreground">
            {row.original.description ?? "—"}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions
            onShow={() => go({ to: showUrl("roles", row.original.name) })}
            onEdit={() => go({ to: editUrl("roles", row.original.name) })}
            hideDelete
          />
        ),
      },
    ],
    [go, showUrl, editUrl],
  );

  const table = useTable<Role, HttpError>({
    columns,
    refineCoreProps: {
      resource: "roles",
      pagination: { pageSize: 25 },
      sorters: { initial: [{ field: "name", order: "asc" }] },
      queryOptions: { retry: false },
      errorNotification: false,
    },
  });

  const core = table.refineCore as unknown as RefineTableCore;

  if (core.tableQuery.isError) {
    return (
      <ProposedListPage
        title="Roles"
        description="The RBAC roles assignable to users (governance taxonomy)."
        icon={Shield}
        endpoint="/api/roles"
        testId="roles-proposed"
      />
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Roles"
        description="The RBAC roles assignable to users (governance taxonomy)."
      />

      <DataTable
        table={table}
        core={core}
        columns={columns}
        emptyMessage="No roles defined yet."
        onRowClick={(r) => go({ to: showUrl("roles", r.name) })}
      />
    </div>
  );
}

export default RoleList;
